import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, forgotPassword, resetPassword } from '../api.js';
import { LOGIN_PATH, passwordProblem } from '../lib/auth.js';
import GirisKabugu, {
  AnimateIn,
  AlanEtiketi,
  HataSeridi,
  KartAciklama,
  KartBaslik,
  MarkaButonu,
  MetinAlani,
  ParolaAlani,
  ParolaGucu,
  UST_HAP,
} from '../components/GirisKabugu.js';

/**
 * §73.3 — parola sıfırlama: TALEP (`/parola-sifirla`) ve YENİ PAROLA
 * (`/parola-yenile?token=…`) sayfaları.
 *
 * GÖRSEL DİL `GirisKabugu` ile referans admin paneline taşındı; DAVRANIŞ, tüm
 * `data-testid`ler ve kullanıcı metinleri önceki sürümle birebir aynıdır (DOM
 * testleri metinlere kilitlidir: "kayıtlıysa", "Tüm cihazlardaki oturumlarınız
 * kapatıldı", "Yeni bir sıfırlama isteyin").
 *
 * Guard'ın DIŞINDADIR: parolasını unutan kullanıcının tanım gereği oturumu yoktur
 * (giriş/davet sayfalarıyla aynı gerekçe, `PUBLIC_PATHS`).
 *
 * HESAP KEŞFİ KORUMASI panelde de sürer: talep ekranı, adresin kayıtlı OLUP
 * OLMADIĞINI söylemez — sunucu zaten iki durumda da aynı gövdeyi döner (§73.3) ve
 * panelin "böyle bir kullanıcı yok" demesi o korumayı tek satırda delerdi.
 */

/** Üst çubuk sağı — iki sayfada da girişe dönüş hapı. */
function GiriseDonHapi() {
  return (
    <Link to={LOGIN_PATH} className={UST_HAP}>
      Girişe dön
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Talep                                                                       */
/* -------------------------------------------------------------------------- */

export function ParolaSifirlaTalep() {
  const [email, setEmail] = useState('');

  const gonder = useMutation({
    mutationFn: () => forgotPassword(email.trim().toLowerCase()),
  });

  /*
   * 429 DIŞINDA hata gösterilmez: uç zaten her durumda 200 döner. Ağ hatası olursa
   * kullanıcıya tekrar denemesi söylenir; "bu adres kayıtlı değil" gibi bir mesaj
   * SUNUCUDAN GELMEZ ve panel de uydurmaz.
   */
  const hata =
    gonder.error instanceof ApiError && gonder.error.status === 429
      ? 'Çok fazla deneme yaptınız. Bir süre sonra tekrar deneyin.'
      : gonder.error !== null && gonder.error !== undefined
        ? 'İstek gönderilemedi — bağlantınızı kontrol edip tekrar deneyin.'
        : null;

  return (
    <GirisKabugu testId="sifirlama-sayfasi" duzen="ortala" ustSag={<GiriseDonHapi />}>
      {gonder.isSuccess ? (
        <div data-testid="sifirlama-gonderildi" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Postanızı kontrol edin</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60} className="flex flex-col gap-4">
            <KartAciklama>
              {/*
                "Gönderdik" DEĞİL "kayıtlıysa gönderildi": sunucunun sözleşmesi budur ve
                panel ondan fazlasını söyleyemez. Aksi hâlde bu ekran, adres yoklayan
                birine "bu hesap var" bilgisini verirdi.
              */}
              <span className="font-medium text-ink">{email.trim().toLowerCase()}</span> adresi
              kayıtlıysa parola sıfırlama bağlantısı gönderildi. Bağlantı 24 saat geçerli ve
              tek kullanımlıktır.
            </KartAciklama>
            <p className="font-grotesk text-[12px] text-[#686868]">
              Mektup birkaç dakika içinde gelmezse gereksiz (spam) klasörünü kontrol edin.
            </p>
            <Link
              to={LOGIN_PATH}
              className="font-grotesk text-[12px] font-normal text-brand hover:underline"
            >
              Girişe dön
            </Link>
          </AnimateIn>
        </div>
      ) : (
        <form
          className="flex flex-col gap-6"
          data-testid="sifirlama-talep-formu"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (email.trim() !== '' && !gonder.isPending) gonder.mutate();
          }}
        >
          <AnimateIn delay={0} className="flex flex-col gap-1.5">
            <KartBaslik>Parolamı unuttum</KartBaslik>
            <KartAciklama>
              Hesabınızın e-posta adresini girin; sıfırlama bağlantısını gönderelim.
            </KartAciklama>
          </AnimateIn>

          <AnimateIn delay={60} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <AlanEtiketi>E-posta</AlanEtiketi>
              <MetinAlani
                data-testid="sifirlama-eposta"
                type="email"
                autoFocus
                autoComplete="email"
                value={email}
                maxLength={254}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="ad.soyad@sirket.com"
              />
            </div>

            {hata !== null ? <HataSeridi testId="sifirlama-hata">{hata}</HataSeridi> : null}

            <MarkaButonu
              type="submit"
              data-testid="sifirlama-gonder"
              disabled={email.trim() === '' || gonder.isPending}
            >
              {gonder.isPending ? 'Gönderiliyor…' : 'Sıfırlama bağlantısı gönder'}
            </MarkaButonu>
          </AnimateIn>

          <AnimateIn delay={120}>
            <Link
              to={LOGIN_PATH}
              className="font-grotesk text-[12px] font-normal text-[#686868] transition-colors hover:text-ink"
            >
              Girişe dön
            </Link>
          </AnimateIn>
        </form>
      )}
    </GirisKabugu>
  );
}

/* -------------------------------------------------------------------------- */
/* Yeni parola                                                                 */
/* -------------------------------------------------------------------------- */

export function ParolaYenile() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [parola, setParola] = useState('');

  const kaydet = useMutation({
    mutationFn: () => resetPassword(token, parola),
  });

  const politika = parola === '' ? null : passwordProblem(parola);
  const hata =
    kaydet.error instanceof ApiError
      ? kaydet.error.code === 'invalid_token'
        ? 'Bu bağlantı geçersiz ya da süresi dolmuş. Yeni bir sıfırlama isteyin.'
        : kaydet.error.message
      : kaydet.error !== null && kaydet.error !== undefined
        ? 'Parola değiştirilemedi — tekrar deneyin.'
        : null;

  if (token === '') {
    return (
      <GirisKabugu testId="yenile-sayfasi" duzen="ortala" ustSag={<GiriseDonHapi />}>
        <div data-testid="yenile-token-yok" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Bağlantı eksik</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60} className="flex flex-col gap-4">
            <KartAciklama>Bu sayfa yalnız e-postadaki sıfırlama bağlantısıyla açılır.</KartAciklama>
            <Link
              to={LOGIN_PATH}
              className="font-grotesk text-[12px] font-normal text-brand hover:underline"
            >
              Girişe dön
            </Link>
          </AnimateIn>
        </div>
      </GirisKabugu>
    );
  }

  return (
    <GirisKabugu testId="yenile-sayfasi" duzen="ortala" ustSag={<GiriseDonHapi />}>
      {kaydet.isSuccess ? (
        <div data-testid="yenile-basarili" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Parolanız değişti</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60} className="flex flex-col gap-4">
            <KartAciklama>
              {/*
                TÜM oturumların düştüğü SÖYLENİR: sıfırlamayı yapan kişi hesabı ele
                geçiren biri olabilir ve gerçek sahibin açık oturumu kalmamalıdır
                (§73.3). Kullanıcı bunu bilmezse "neden diğer cihazımdan atıldım"
                sorusuyla kalırdı.
              */}
              Tüm cihazlardaki oturumlarınız kapatıldı. Yeni parolanızla giriş yapın.
            </KartAciklama>
            <MarkaButonu
              type="button"
              data-testid="yenile-girise-git"
              onClick={() => navigate(LOGIN_PATH, { replace: true })}
            >
              Girişe git
            </MarkaButonu>
          </AnimateIn>
        </div>
      ) : (
        <form
          className="flex flex-col gap-6"
          data-testid="yenile-formu"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (parola !== '' && politika === null && !kaydet.isPending) kaydet.mutate();
          }}
        >
          <AnimateIn delay={0}>
            <KartBaslik>Yeni parola belirleyin</KartBaslik>
          </AnimateIn>

          <AnimateIn delay={60} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <AlanEtiketi>Yeni parola</AlanEtiketi>
              <ParolaAlani
                data-testid="yenile-parola"
                autoFocus
                autoComplete="new-password"
                value={parola}
                onChange={(event) => setParola(event.target.value)}
                placeholder="••••••••••"
              />
              {/* Referans set-password ekranındaki güç barları — karar vermez, yalnız söyler. */}
              <ParolaGucu parola={parola} />
            </div>

            {politika !== null ? (
              <p data-testid="yenile-politika" className="font-grotesk text-[12px] text-[#b45309]">
                {politika}
              </p>
            ) : null}
            {hata !== null ? <HataSeridi testId="yenile-hata">{hata}</HataSeridi> : null}

            <MarkaButonu
              type="submit"
              data-testid="yenile-kaydet"
              disabled={parola === '' || politika !== null || kaydet.isPending}
            >
              {kaydet.isPending ? 'Kaydediliyor…' : 'Parolayı değiştir'}
            </MarkaButonu>
          </AnimateIn>
        </form>
      )}
    </GirisKabugu>
  );
}
